import type { ApiSeasonStat } from "../../../../api";

/**
 * Display rules for the season-stat tiles on the player profile.
 *
 * The backend sends measurements. Everything about how a tile READS is decided
 * here: rounding, the empty value, and the label text. That is the point of
 * P3-135 -- `player.season_stats` is a stored column, so a display rule left in
 * an adapter only reaches a fan when that player's row is next rewritten, which
 * between seasons can be months.
 *
 * See `docs/season-stats.md`.
 */

/** An absent measurement. Not a hyphen: the em dash reads as "no value". */
const EM_DASH = "—";

/**
 * Label per code, overriding the one on the wire.
 *
 * The three-column grid uses `repeat(3, minmax(max-content, 1fr))`, so a long
 * label widens its column instead of truncating and can overflow the `size="sm"`
 * dialog. EVERY label here is 8 characters or fewer. That budget belongs with
 * the grid, which is here, not in three adapters that cannot see it.
 *
 * A code missing from this table falls back to the label the backend sent, so a
 * new stat still ships without a frontend deploy.
 *
 * `POINTS` is deliberately absent. The NFL adapter means a kicker's points
 * (`PTS`) and the NHL adapter means a skater's points (`P`). One code, two
 * labels, so the wire label is the only one that can be right.
 */
export const SEASON_STAT_LABELS: Record<string, string> = {
  // NFL
  PASSING_YARDS: "Pass Yds",
  PASSING_TOUCHDOWNS: "Pass TD",
  INTERCEPTIONS_THROWN: "INT",
  COMPLETION_PCT: "CMP%",
  PASSER_RATING: "RTG",
  RUSHING_YARDS: "Rush Yds",
  RUSHING_TOUCHDOWNS: "Rush TD",
  RUSHING_ATTEMPTS: "ATT",
  YARDS_PER_CARRY: "YPC",
  RECEPTIONS: "REC",
  RECEIVING_YARDS: "Rec Yds",
  RECEIVING_TOUCHDOWNS: "Rec TD",
  YARDS_PER_RECEPTION: "YPR",
  LONG_RECEPTION: "LNG",
  TARGETS: "TGT",
  TOTAL_TACKLES: "TCKL",
  SOLO_TACKLES: "SOLO",
  SACKS: "SACK",
  INTERCEPTIONS: "INT",
  PASSES_DEFENSED: "PD",
  FORCED_FUMBLES: "FF",
  FIELD_GOALS: "FG",
  FIELD_GOAL_PCT: "FG%",
  LONG_FIELD_GOAL: "LNG",
  EXTRA_POINTS: "XP",
  FIELD_GOALS_50_PLUS: "50+",
  PUNTS: "Punts",
  PUNT_AVG: "AVG",
  PUNT_NET_AVG: "NET",
  LONG_PUNT: "LNG",
  PUNTS_INSIDE_20: "In 20",
  TOUCHBACKS: "TB",
  // MLB
  BATTING_AVG: "AVG",
  HOME_RUNS: "HR",
  RUNS_BATTED_IN: "RBI",
  RUNS: "R",
  ON_BASE_PERCENTAGE: "OBP",
  HITS: "H",
  // NHL
  GOALS_AGAINST_AVG: "GAA",
  SAVE_PCT: "SV%",
  WINS: "W",
  SHUTOUTS: "SO",
  GAMES_PLAYED: "GP",
  SAVES: "Saves",
  GOALS: "G",
  ASSISTS: "A",
  PLUS_MINUS: "+/-",
  POWER_PLAY_GOALS: "PPG",
  SHOTS_ON_GOAL: "SOG",
};

/**
 * How many decimals a rate shows, and whether it drops its leading zero.
 *
 * A rate under 1 that a fan reads as a proportion is written `.287`, never
 * `0.287`. That is the convention for a batting average, an on-base percentage
 * and a save percentage. A goals-against average is a count per game, so it
 * keeps two decimals and any leading digit.
 *
 * A rate code missing from this table gets one decimal with its leading zero,
 * which suits every percentage, rating and per-attempt average the adapters
 * send today.
 */
const RATE_STYLES: Record<
  string,
  { decimals: number; dropLeadingZero?: boolean }
> = {
  BATTING_AVG: { decimals: 3, dropLeadingZero: true },
  ON_BASE_PERCENTAGE: { decimals: 3, dropLeadingZero: true },
  SAVE_PCT: { decimals: 3, dropLeadingZero: true },
  GOALS_AGAINST_AVG: { decimals: 2 },
};

const DEFAULT_RATE_STYLE = { decimals: 1, dropLeadingZero: false };

const formatRate = (code: string, value: number): string => {
  const { decimals, dropLeadingZero } = RATE_STYLES[code] ?? DEFAULT_RATE_STYLE;
  const text = value.toFixed(decimals);
  return dropLeadingZero ? text.replace(/^(-?)0\./, "$1.") : text;
};

/** The label to show, preferring this app's text over the backend's. */
export const seasonStatLabel = (stat: ApiSeasonStat): string =>
  SEASON_STAT_LABELS[stat.code] ?? stat.label;

/**
 * The value to show.
 *
 * TOLERANT READ -- two wire shapes reach this function and both must render.
 *
 * A tile with no `format` is the LEGACY shape, where the backend had already
 * rendered the value to text (`".000"`, `"24/27"`, `"96.4"`). There is nothing
 * left to decide, so it passes through. That covers a stored row written before
 * the P3-135 migration, and a row written by a backend that has not yet
 * deployed.
 *
 * A tile WITH a `format` carries a measurement, and the rules below apply.
 * A `null` measurement means the feed said nothing:
 *
 *   - a count renders `0`, because "no tackles recorded" is zero tackles;
 *   - a rate renders an em dash, because an average over zero attempts is not
 *     `.000`, it is nothing.
 */
export const formatSeasonStatValue = (stat: ApiSeasonStat): string => {
  if (stat.format === undefined) {
    return stat.value === null ? EM_DASH : String(stat.value);
  }

  if (stat.format === "ratio") {
    const parts = stat.parts;
    if (!parts) return EM_DASH;
    return `${parts.made}/${parts.attempts}`;
  }

  // A legacy string can still arrive under a new `format` if a stored row was
  // only half converted. Reading it as a number keeps the tile correct.
  const value =
    typeof stat.value === "string" ? Number.parseFloat(stat.value) : stat.value;

  if (value === null || value === undefined || !Number.isFinite(value)) {
    return stat.format === "count" ? "0" : EM_DASH;
  }

  return stat.format === "count"
    ? String(Math.round(value))
    : formatRate(stat.code, value);
};
